/**
 * Caption geometry for the six-view orthographic sheet.
 *
 * The sheet image is deliberately text-free. Its six panels are composited
 * server-side (backend/orthographic_sheet.py) into a fixed 2x3 grid, and the
 * captions live here instead of in the pixels — the image model could not spell
 * Turkish and mislabelled its own panels, returning nine of them with FRONT
 * identical to LEFT.
 *
 * GEOMETRY IS SHARED WITH THE BACKEND. These numbers mirror the Python
 * defaults (cell=512, gap=16, margin=16 -> a 1600x1072 sheet). Both sides pin
 * them in tests, so changing one without the other fails CI rather than
 * silently sliding every caption off its panel.
 */

export const SHEET_WIDTH = 1600;
export const SHEET_HEIGHT = 1072;
export const CELL = 512;
export const GAP = 16;
export const MARGIN = 16;
export const COLUMNS = 3;
export const ROWS = 2;

/** Row-major. Position is a panel's only identifier, so this order is a contract. */
export const PANEL_ORDER = ['front', 'rear', 'right', 'left', 'top', 'bottom'];

export const PANEL_LABELS = {
  front: { en: 'FRONT', tr: 'ÖN' },
  rear: { en: 'REAR', tr: 'ARKA' },
  right: { en: 'RIGHT', tr: 'SAĞ' },
  left: { en: 'LEFT', tr: 'SOL' },
  top: { en: 'TOP', tr: 'ÜST' },
  bottom: { en: 'BOTTOM', tr: 'ALT' },
};

/**
 * Percentage rect of each panel cell, for absolute positioning over the image.
 * Percentages rather than pixels so the sheet can be displayed at any width.
 */
export function panelRects() {
  return PANEL_ORDER.map((name, index) => {
    const row = Math.floor(index / COLUMNS);
    const col = index % COLUMNS;
    const x = MARGIN + col * (CELL + GAP);
    const y = MARGIN + row * (CELL + GAP);
    return {
      name,
      leftPct: (x / SHEET_WIDTH) * 100,
      topPct: (y / SHEET_HEIGHT) * 100,
      widthPct: (CELL / SHEET_WIDTH) * 100,
      heightPct: (CELL / SHEET_HEIGHT) * 100,
    };
  });
}

/** Caption for a panel in the reader's language, falling back to English. */
export function panelLabel(name, language) {
  const entry = PANEL_LABELS[name];
  if (!entry) return '';
  return language === 'tr' ? entry.tr : entry.en;
}
